import { styled, useTheme } from 'styled-components';
import React, {
  DetailedHTMLProps,
  FC,
  InputHTMLAttributes,
  ReactNode,
  useState,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { Styling } from '@/styling';
import { flexGap } from '@/flexGap';
import { Box } from '@/components';

// TODO convert styled component object style to string style

type LabelProps = {
  disabled?: boolean;
};

const Label = styled.label<LabelProps>`
  ${Styling.typography('body')}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  ${({ theme }) => flexGap(theme.spacing[1])};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  user-select: none;
`;

type SquareProps = {
  hover?: boolean;
  checked?: boolean;
  disabled?: boolean;
  focus?: boolean;
};

const BareSquare = styled.div<SquareProps>(
  ({ hover, checked, disabled, focus, theme }) => ({
    position: 'relative',
    visibility: 'visible',
    display: 'flex',
    flexShrink: 0,
    alignSelf: 'start',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    color: theme.palette.primary.main,
    border: `2px solid ${
      focus ? theme.palette.primary.main : theme.palette.grayscale.light2
    }`, // TODO move to theme
    borderRadius: theme.radii.input,
    opacity: disabled ? 0.5 : 1,
    backgroundColor: disabled
      ? theme.palette.grayscale.light4
      : theme.palette.grayscale.white,
    ...(hover && !disabled
      ? {
          backgroundColor: theme.palette.grayscale.light5,
        }
      : {}),
    ...(checked && !disabled
      ? {
          border: focus ? `2px solid ${theme.palette.primary.light2}` : 0,
        }
      : {}),
  }),
);

const FilledSquare = styled.div<SquareProps>(
  ({ hover, checked, disabled, focus, theme }) => ({
    position: 'relative',
    visibility: 'visible',
    display: 'flex',
    flexShrink: 0,
    alignSelf: 'start',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    color: theme.palette.grayscale.white,
    border: `2px solid ${
      focus ? theme.palette.primary.main : theme.palette.grayscale.light2
    }`, // TODO move to theme
    borderRadius: theme.radii.input,
    opacity: disabled ? 0.5 : 1,
    backgroundColor: disabled
      ? theme.palette.grayscale.light4
      : theme.palette.grayscale.white,
    ...(hover && !disabled
      ? {
          backgroundColor: theme.palette.grayscale.light5,
        }
      : {}),
    ...(checked && !disabled
      ? {
          backgroundColor: `${theme.palette.primary.main} !important`,
          border: focus ? `2px solid ${theme.palette.primary.light2}` : 0,
        }
      : {}),
    transition: '100ms background-color, 100ms border',
  }),
);

const Input = styled.input({
  position: 'absolute',
  opacity: 0,
  height: 0,
  width: 0,
  cursor: 'pointer',
});

const Checkmark = styled.span<SquareProps>(({ disabled, theme }) => ({
  position: 'absolute',
  color: `${disabled ? theme.palette.grayscale.dark3 : 'inherit'}`,
}));

export type CheckboxProps = {
  className?: string;
  name?: string;
  icon?: ReactNode;
  label?: ReactNode;
} & DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

export const BareCheckbox: FC<CheckboxProps> = ({
  className,
  checked,
  disabled,
  label,
  ref: _ref,
  icon,
  ...props
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const isChecked = checked ?? Boolean(props.value);

  return (
    <Label
      className={className}
      disabled={disabled}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <BareSquare
        hover={isHovering}
        checked={isChecked}
        disabled={disabled}
        focus={hasFocus}
      >
        {isChecked && (
          <Checkmark
            hover={isHovering}
            checked={isChecked}
            disabled={disabled}
            focus={hasFocus}
          >
            {icon ?? <FontAwesomeIcon icon={faCheck} size="lg" />}
          </Checkmark>
        )}
      </BareSquare>
      {label}
      <Input
        type="checkbox"
        {...props}
        checked={isChecked}
        disabled={disabled}
        onFocus={(evt) => {
          setHasFocus(true);
          props.onFocus?.(evt);
        }}
        onBlur={(evt) => {
          setHasFocus(false);
          props.onBlur?.(evt);
        }}
      />
    </Label>
  );
};

export const FilledCheckbox: FC<CheckboxProps> = ({
  className,
  checked,
  disabled,
  label,
  ref: _ref,
  icon,
  ...props
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const isChecked = checked ?? Boolean(props.value);

  return (
    <Label
      className={className}
      disabled={disabled}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <FilledSquare
        hover={isHovering}
        checked={isChecked}
        disabled={disabled}
        focus={hasFocus}
      >
        {isChecked && (
          <Checkmark
            hover={isHovering}
            checked={isChecked}
            disabled={disabled}
            focus={hasFocus}
          >
            {icon ?? <FontAwesomeIcon icon={faCheck} size="lg" />}
          </Checkmark>
        )}
      </FilledSquare>
      {label}
      <Input
        type="checkbox"
        {...props}
        checked={isChecked}
        disabled={disabled}
        onFocus={(evt) => {
          setHasFocus(true);
          props.onFocus?.(evt);
        }}
        onBlur={(evt) => {
          setHasFocus(false);
          props.onBlur?.(evt);
        }}
      />
    </Label>
  );
};

export type MultiCheckboxProps = {
  className?: string;
  onChange?(boolean: string[]): void;
  values?: string[];
  options: {
    icon?: ReactNode;
    label: string;
  }[];
  disabled?: boolean;
};

export const BareMultiCheckbox: FC<MultiCheckboxProps> = ({
  className,
  onChange,
  values = [],
  options,
  disabled,
}) => {
  const theme = useTheme();

  const handleChange = (label: string) => {
    const result = values.slice();
    const index = result.indexOf(label);

    if (index > -1) {
      result.splice(index, 1);
    } else {
      result.push(label);
    }

    onChange?.(result);
  };

  return (
    <Box
      className={className}
      direction="vertical"
      alignX="left"
      spacing={theme.spacing[2]}
    >
      {options.map((option, index) => (
        <BareCheckbox
          key={option.label + index}
          disabled={disabled}
          icon={option.icon}
          label={option.label}
          checked={values.includes(option.label)}
          onChange={() => handleChange(option.label)}
        />
      ))}
    </Box>
  );
};

export const FilledMultiCheckbox: FC<MultiCheckboxProps> = ({
  className,
  onChange,
  values = [],
  options,
  disabled,
}) => {
  const theme = useTheme();

  const handleChange = (label: string) => {
    const result = values.slice();
    const index = result.indexOf(label);

    if (index > -1) {
      result.splice(index, 1);
    } else {
      result.push(label);
    }

    onChange?.(result);
  };

  return (
    <Box
      className={className}
      direction="vertical"
      alignX="left"
      spacing={theme.spacing[2]}
    >
      {options.map((option, index) => (
        <FilledCheckbox
          key={option.label + index}
          disabled={disabled}
          icon={option.icon}
          label={option.label}
          checked={values.includes(option.label)}
          onChange={() => handleChange(option.label)}
        />
      ))}
    </Box>
  );
};
